import type { JSONPatchOperation } from 'immutable-json-patch'
import { fieldChanges, type FieldRow } from './fields'
import { kindOf, pointerOf, type StructuredField } from './groups'
import { valueChoices } from './labels'
import type { StructuredValue } from './structured'

type Option = { value: string; label: string }
export type FieldControl = { kind: 'text'; value: string } | { kind: 'number'; value: number }
  | { kind: 'boolean'; value: boolean } | { kind: 'choice'; value: string; options: Option[] }
  | { kind: 'slider'; value: string; position: number; options: Option[] }
  | { kind: 'modules'; value: string[]; options: Option[] } | { kind: 'list'; value: string[] }
  | { kind: 'readonly'; value: StructuredValue }
export type FieldControlEdit = { kind: 'replace'; value: StructuredValue } | { kind: 'modules'; value: string[] }
  | { kind: 'list-item'; index: number; value: string } | { kind: 'append' } | { kind: 'remove'; index: number }
type SettingGroup = 'filling' | 'collection' | 'recognition' | 'other'
export type FieldSection = { key: SettingGroup; label: string | null; fields: { field: StructuredField; label: string }[] }
type Input = { root: StructuredValue; field: StructuredField }
type Resolver = (input: Input) => FieldControl
type ControlOf<K extends FieldControl['kind']> = Extract<FieldControl, { kind: K }>
const record = (value: StructuredValue): value is { [key: string]: StructuredValue } =>
  value !== null && typeof value === 'object' && !Array.isArray(value)
const strings = (value: StructuredValue): value is string[] => Array.isArray(value) && value.every((item) => typeof item === 'string')
const readonly: Resolver = ({ field }) => ({ kind: 'readonly', value: field.value })

const modules: Resolver = ({ root, field }) => {
  const value = field.value
  if (!strings(value) || new Set(value).size !== value.length) return readonly({ root, field })
  const declarations = record(root) ? root.module_field_sets : null
  const keys = record(declarations ?? null) ? Object.keys(declarations!) : []
  const options = [...new Set([...keys, ...value])].map((key) => ({ value: key, label: key }))
  return { kind: 'modules', value, options }
}
const choice: Resolver = ({ root, field }) => {
  if (typeof field.value !== 'string') return readonly({ root, field })
  const options = valueChoices(field.path, field.value)
  if (!options) return readonly({ root, field })
  return { kind: 'choice', value: field.value, options }
}
const criticality: Resolver = ({ root, field }) => {
  const options = valueChoices(['criticality'], 'critical') ?? []
  const position = options.findIndex(({ value }) => value === field.value)
  if (typeof field.value !== 'string' || position < 0) return readonly({ root, field })
  return { kind: 'slider', value: field.value, position, options }
}

/** Paths select presentation only; no control merges the independent definition and semantics policies. */
const POLICIES = new Map<string, Resolver>([
  ['field_definitions:priority', choice], ['field_definitions:modules', modules],
  ['field_semantics:value_type', choice], ['field_semantics:criticality', criticality],
  ['field_semantics:modules', modules], ['field_semantics:required_by_modules', modules],
])
const SECTIONS: { key: SettingGroup; label: string | null }[] = [
  { key: 'filling', label: '填写设置' }, { key: 'collection', label: '收集设置' },
  { key: 'recognition', label: '识别设置' }, { key: 'other', label: null },
]
const SETTINGS = new Map<string, { group: SettingGroup; label?: string }>([
  ['field_definitions:priority', { group: 'filling' }], ['field_definitions:blocker', { group: 'filling' }],
  ['field_semantics:value_type', { group: 'filling' }], ['field_semantics:criticality', { group: 'filling' }],
  ['field_semantics:data_standard', { group: 'filling' }], ['field_semantics:example', { group: 'filling' }],
  ['field_semantics:allowed_units', { group: 'filling' }], ['field_semantics:currency', { group: 'filling' }],
  ['field_semantics:currency_unit', { group: 'filling' }],
  ['field_definitions:question', { group: 'collection' }], ['field_definitions:options', { group: 'collection' }],
  ['field_definitions:modules', { group: 'collection', label: '收集模块' }],
  ['field_semantics:modules', { group: 'collection', label: '适用模块' }],
  ['field_semantics:required_by_modules', { group: 'collection' }],
  ['field_definitions:extraction_patterns', { group: 'recognition' }],
  ['field_definitions:value_exclusion_patterns', { group: 'recognition' }],
  ['field_definitions:aliases', { group: 'recognition' }], ['field_definitions:value_aliases', { group: 'recognition' }],
])
/** Group presentation without merging source bindings; undeclared attributes remain visible, unclassified. */
export const fieldSections = (row: FieldRow): FieldSection[] => {
  const fields = row.details.map((field) => {
    const setting = SETTINGS.get(`${field.path[0]}:${field.path[2]}`)
    return { field, label: setting?.label ?? field.label, group: setting?.group ?? 'other' }
  })
  return SECTIONS.map((section) => ({ ...section, fields: fields.filter(({ group }) => group === section.key) }))
    .filter((section) => section.fields.length > 0)
}
const SCALARS: Partial<Record<ReturnType<typeof kindOf>, Resolver>> = {
  string: ({ field }) => ({ kind: 'text', value: field.value as string }),
  number: ({ field }) => ({ kind: 'number', value: field.value as number }),
  boolean: ({ field }) => ({ kind: 'boolean', value: field.value as boolean }),
  array: (input) => strings(input.field.value) ? { kind: 'list', value: input.field.value } : readonly(input),
}
export const fieldControl = (root: StructuredValue, field: StructuredField): FieldControl => {
  const path = field.path
  const policy = path.length === 3 ? POLICIES.get(`${path[0]}:${path[2]}`) : undefined
  return (policy ?? SCALARS[kindOf(field.value)] ?? readonly)({ root, field })
}

type EditInput<E extends FieldControlEdit = FieldControlEdit> = { field: StructuredField; control: FieldControl; edit: E }
const replaceable: Partial<Record<FieldControl['kind'], (control: FieldControl, value: StructuredValue) => boolean>> = {
  text: (_control, value) => typeof value === 'string',
  number: (_control, value) => typeof value === 'number' && Number.isFinite(value) && (!Number.isInteger(value) || Number.isSafeInteger(value)),
  boolean: (_control, value) => typeof value === 'boolean',
  choice: (control, value) => (control as ControlOf<'choice'>).options.some((option) => option.value === value),
  slider: (control, value) => (control as ControlOf<'slider'>).options.some((option) => option.value === value),
}
const replace = ({ field, control, edit }: EditInput<Extract<FieldControlEdit, { kind: 'replace' }>>): JSONPatchOperation[] => {
  if (!replaceable[control.kind]?.(control, edit.value)) throw new Error('此配置值不可应用')
  return [{ op: 'replace', path: pointerOf(field.path), value: edit.value }]
}
const indexOf = (control: FieldControl, index: number): string[] => {
  if (control.kind !== 'list' || !Number.isInteger(index) || index < 0 || index >= control.value.length) throw new Error('列表条目已变化')
  return control.value
}
const listItem = ({ field, control, edit }: EditInput<Extract<FieldControlEdit, { kind: 'list-item' }>>): JSONPatchOperation[] => {
  indexOf(control, edit.index)
  if (typeof edit.value !== 'string') throw new Error('列表条目必须为文本')
  return [{ op: 'replace', path: pointerOf([...field.path, edit.index]), value: edit.value }]
}
const append = ({ field, control }: EditInput): JSONPatchOperation[] => {
  if (control.kind !== 'list') throw new Error('此配置不是文本列表')
  return [{ op: 'add', path: pointerOf([...field.path, '-']), value: '' }]
}
const remove = ({ field, control, edit }: EditInput<Extract<FieldControlEdit, { kind: 'remove' }>>): JSONPatchOperation[] => {
  indexOf(control, edit.index)
  return [{ op: 'remove', path: pointerOf([...field.path, edit.index]) }]
}
const membership = ({ field, control, edit }: EditInput<Extract<FieldControlEdit, { kind: 'modules' }>>): JSONPatchOperation[] => {
  if (control.kind !== 'modules' || !strings(edit.value) || new Set(edit.value).size !== edit.value.length
    || !edit.value.every((value) => control.options.some((option) => option.value === value))) throw new Error('模块选项已变化')
  const selected = new Set(edit.value)
  const previous = new Set(control.value)
  const removed: JSONPatchOperation[] = control.value.map((value, index) => ({ value, index }))
    .filter(({ value }) => !selected.has(value)).reverse().map(({ index }) => ({ op: 'remove', path: pointerOf([...field.path, index]) }))
  const added: JSONPatchOperation[] = edit.value.filter((value) => !previous.has(value))
    .map((value) => ({ op: 'add', path: pointerOf([...field.path, '-']), value }))
  return [...removed, ...added]
}
const EDITS = { replace, modules: membership, 'list-item': listItem, append, remove }

/** Reuse the field owner's guards, then patch individual list nodes so surviving YAML comments stay attached. */
export const fieldControlChanges = (root: StructuredValue, key: string, field: StructuredField, edit: FieldControlEdit): JSONPatchOperation[] => {
  const guards = fieldChanges(root, key, field, field.value).slice(0, -1)
  const control = fieldControl(root, field)
  const dispatch = EDITS[edit.kind] as (input: EditInput) => JSONPatchOperation[]
  return [...guards, ...dispatch({ field, control, edit })]
}
